import { GatsbyImage } from "gatsby-plugin-image";
import styled from "styled-components";

import {
  AspectRatio,
  Box,
  Card,
  Heading,
  Stack,
  Text,
} from "@vinomofo/components";

import { StorefrontImage } from "../../../types";

const DealerCardStack = styled(Stack)`
  cursor: pointer;
  max-width: 250px;
  max-height: 480px;

  display: flex;
  justify-content: center;
  flex-shrink: 0;
`;

export type Dealer = {
  name: string;
  image: StorefrontImage;
  blurb?: string;
  likes?: string;
  dislikes?: string;
};

export interface DealerCardProps {
  dealer: Dealer;
  isOpen?: boolean;
  toggleOpen: () => void;
}

export const DealerCardContent = ({
  dealer,
  borderColor,
}: Pick<DealerCardProps, "dealer"> & { borderColor: string }): JSX.Element => {
  const { name, blurb, image } = dealer;

  return (
    <Box width="100%" display="flex" justifyContent="center" minWidth="250px">
      <DealerCardStack space={1} textAlign="center">
        {image && (
          <AspectRatio
            ratio={1}
            borderRadius="100%"
            border="5px solid"
            borderColor={borderColor}
          >
            <GatsbyImage
              alt={`Photo of ${name}`}
              image={image}
              objectFit="cover"
              style={{
                borderRadius: "100%",
                marginBottom: 12,
                width: "100%",
                height: "100%",
              }}
            />
          </AspectRatio>
        )}
        <Heading variant="xs">{name}</Heading>
        <Text variant="sm">{blurb}</Text>
      </DealerCardStack>
    </Box>
  );
};

const DealerPopover = ({
  dealer,
}: Pick<DealerCardProps, "dealer">): JSX.Element => {
  return (
    <Card backgroundColor="white">
      <Box width="100%" padding={4}>
        {dealer.likes && (
          <Text variant="sm">
            <Heading color="brand" as="strong" variant="xxs">
              Nices:
            </Heading>{" "}
            {dealer.likes}
          </Text>
        )}
        <Box
          borderTop="1px solid"
          borderTopColor="divider"
          width="100%"
          my={2}
        />
        {dealer.dislikes && (
          <Text variant="sm">
            <Heading color="redRedWine" as="strong" variant="xxs">
              Vices:
            </Heading>{" "}
            {dealer.dislikes}
          </Text>
        )}
      </Box>
    </Card>
  );
};

export const DealerCard = ({
  dealer,
  isOpen,
  toggleOpen,
}: DealerCardProps): JSX.Element => {
  return (
    <Box position="relative" onClick={toggleOpen}>
      <DealerCardContent
        dealer={dealer}
        borderColor={isOpen ? "brand" : "white"}
      />
      {isOpen && (
        <Box
          position="absolute"
          top="auto"
          bottom={0}
          left={0}
          right={0}
          z-index={2}
        >
          <DealerPopover dealer={dealer} />
        </Box>
      )}
    </Box>
  );
};
